import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { X, Loader2, MapPin, Clock3 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

// Leaflet marker icon fix (bundlers break the default icon URLs)
L.Icon.Default.mergeOptions({ iconRetinaUrl: markerIcon2x, iconUrl: markerIcon, shadowUrl: markerShadow });

const TORONTO = [43.6532, -79.3832];

const nowTorontoHM = () =>
  new Date().toLocaleTimeString("en-GB", { timeZone: "America/Toronto", hour12: false, hour: "2-digit", minute: "2-digit" });

/**
 * AddPunchDialog — SUPERADMIN ONLY. Manually add a clock-in / clock-out for
 * any employee on the Operations day view: pick the event, the time, and
 * drop a pin on the map for the location.
 */
const AddPunchDialog = ({ day, employees, onClose, onSaved }) => {
  const [empId, setEmpId] = useState("");
  const [event, setEvent] = useState("clock_in");
  const [date, setDate] = useState(day);
  const [time, setTime] = useState(nowTorontoHM());
  const [jobName, setJobName] = useState("");
  const [pin, setPin] = useState(null); // [lat, lng]
  const [busy, setBusy] = useState(false);
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  useEffect(() => {
    if (!mapEl.current || mapRef.current) return undefined;
    const map = L.map(mapEl.current, { zoomControl: true }).setView(TORONTO, 10);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);
    map.on("click", (e) => {
      const ll = [e.latlng.lat, e.latlng.lng];
      setPin(ll);
      if (markerRef.current) markerRef.current.setLatLng(ll);
      else markerRef.current = L.marker(ll).addTo(map);
    });
    mapRef.current = map;
    // Try to centre on the admin's own position.
    try {
      navigator.geolocation?.getCurrentPosition(
        (p) => map.setView([p.coords.latitude, p.coords.longitude], 14),
        () => {},
        { timeout: 5000 },
      );
    } catch { /* default centre stays */ }
    setTimeout(() => map.invalidateSize(), 150);
    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
  }, []);

  const save = async () => {
    if (!empId) { toast.error("Pick an employee"); return; }
    if (!date || !time) { toast.error("Pick the date and time"); return; }
    if (!pin) { toast.error("Drop a pin on the map for the location"); return; }
    setBusy(true);
    try {
      await api.post("/time-entries", {
        employee_id: empId,
        event,
        timestamp: `${date}T${time}`, // naive → interpreted as Toronto time
        lat: pin[0],
        lng: pin[1],
        job_name: jobName.trim(),
      });
      toast.success(`${event === "clock_in" ? "Clock-in" : "Clock-out"} added`);
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add the punch");
    } finally { setBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[85] flex items-center justify-center bg-black/70 p-3" data-testid="add-punch-dialog">
      <div className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-lg border border-white/10 bg-[#0b0b0f] text-white">
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
          <h3 className="text-lg font-bold uppercase tracking-tight">Add punch (superadmin)</h3>
          <button onClick={onClose} aria-label="Close" data-testid="add-punch-close" className="rounded border border-white/15 bg-white/5 p-1.5 text-white/70"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3 overflow-y-auto p-4">
          <div>
            <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">Employee</label>
            <select
              value={empId}
              onChange={(e) => setEmpId(e.target.value)}
              data-testid="add-punch-employee"
              className="h-10 w-full rounded-md border border-white/15 bg-[#0a0a0a] px-2 text-sm text-white focus:border-[#1a3fd1] focus:outline-none"
            >
              <option value="">— Pick an employee —</option>
              {employees.map((e) => (
                <option key={e.employee_id} value={e.employee_id}>{e.name}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setEvent("clock_in")}
              data-testid="add-punch-event-in"
              className={`rounded-md border px-3 py-2 text-xs font-semibold uppercase tracking-widest ${
                event === "clock_in" ? "border-emerald-400 bg-emerald-500/15 text-emerald-200" : "border-white/15 bg-white/[0.03] text-white/60"
              }`}
            >
              Clock in
            </button>
            <button
              type="button"
              onClick={() => setEvent("clock_out")}
              data-testid="add-punch-event-out"
              className={`rounded-md border px-3 py-2 text-xs font-semibold uppercase tracking-widest ${
                event === "clock_out" ? "border-red-400 bg-red-500/15 text-red-200" : "border-white/15 bg-white/[0.03] text-white/60"
              }`}
            >
              Clock out
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">Date</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="add-punch-date"
                className="h-10 w-full rounded-md border border-white/15 bg-white/5 px-3 text-sm text-white focus:border-[#1a3fd1] focus:outline-none" />
            </div>
            <div>
              <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">Time (Toronto)</label>
              <input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="add-punch-time"
                className="h-10 w-full rounded-md border border-white/15 bg-white/5 px-3 text-sm text-white focus:border-[#1a3fd1] focus:outline-none" />
            </div>
          </div>
          {event === "clock_in" && (
            <div>
              <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">Job label (optional)</label>
              <input value={jobName} onChange={(e) => setJobName(e.target.value)} placeholder="e.g. 123 Main St" data-testid="add-punch-job"
                className="h-10 w-full rounded-md border border-white/15 bg-white/5 px-3 text-sm text-white focus:border-[#1a3fd1] focus:outline-none" />
            </div>
          )}
          <div>
            <label className="mb-1 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-white/50">
              <MapPin className="h-3 w-3" /> Location — tap the map to drop the pin
            </label>
            <div ref={mapEl} className="h-56 w-full rounded-lg border border-white/15" data-testid="add-punch-map" />
            <p className="mt-1 font-mono text-[10px] text-white/40" data-testid="add-punch-pin">
              {pin ? `Pin: ${pin[0].toFixed(5)}, ${pin[1].toFixed(5)}` : "No pin yet"}
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-white/10 px-4 py-3">
          <button onClick={onClose} className="rounded-md border border-white/15 bg-white/5 px-3 py-2 text-xs text-white/70" data-testid="add-punch-cancel">Cancel</button>
          <button
            onClick={save} disabled={busy}
            data-testid="add-punch-save"
            className="inline-flex items-center gap-1.5 rounded-md bg-[#1a3fd1] px-4 py-2 text-xs font-semibold text-white hover:bg-[#1633a3] disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Clock3 className="h-3.5 w-3.5" />} Add punch
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default AddPunchDialog;
